import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Send } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDate, formatDateTime, STATUS_LABELS, STATUS_COLORS } from '../lib/utils'
import { useAuth } from '../context/AuthContext'
import CsvControls from '../components/CsvControls'

interface Customer {
  id: string; full_name: string; phone: string; plan: string
  loyalty_points: number; drops_balance: number; frozen: number; is_vip?: number; created_at: string
  order_count: number; total_spent_fils: number
}

interface CustomerOrder {
  id: string; order_number: string; total_fils: number; status: string; created_at: string
}

interface CustomerDetail {
  customer: Customer
  orders: CustomerOrder[]
}

function CustomerPanel({
  customerId,
  onClose,
  canManage,
  canVip,
}: {
  customerId: string
  onClose: () => void
  canManage: boolean
  canVip: boolean
}) {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery<CustomerDetail>({
    queryKey: ['customer-detail', customerId],
    queryFn: () => api.get(`/customers/${customerId}`).then(r => r.data),
  })

  const [revokedAt, setRevokedAt] = useState<Date | null>(null)
  // Reset the "just revoked" line whenever the drawer swaps to a
  // different customer — otherwise the green tick from customer A's
  // revoke lingers into customer B's drawer.
  useEffect(() => { setRevokedAt(null) }, [customerId])
  const revokeSessions = useMutation({
    mutationFn: (id: string) =>
      api.post(`/customers/${id}/revoke-sessions`),
    onSuccess: () => setRevokedAt(new Date()),
  })
  const toggleFreeze = useMutation({
    mutationFn: ({ id, frozen }: { id: string; frozen: boolean }) =>
      api.patch(`/customers/${id}/freeze`, { frozen }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      qc.invalidateQueries({ queryKey: ['customer-detail', customerId] })
    },
  })
  const toggleVip = useMutation({
    mutationFn: ({ id, is_vip }: { id: string; is_vip: boolean }) =>
      api.post(`/customers/${id}/vip`, { is_vip }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      qc.invalidateQueries({ queryKey: ['customer-detail', customerId] })
      qc.invalidateQueries({ queryKey: ['customer-insights', customerId] })
    },
  })

  const c = data?.customer

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#111] border-l border-[#2e2e2e] h-full overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#2e2e2e] sticky top-0 bg-[#111] z-10">
          <div>
            <p className="text-white font-semibold">{c?.full_name ?? '…'}</p>
            <p className="text-gray-500 text-xs mt-0.5">{c?.phone}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-1 mt-0.5">
            <X size={18} />
          </button>
        </div>

        {isLoading && (
          <div className="flex-1 flex items-center justify-center text-gray-600 text-sm">Loading…</div>
        )}

        {c && (
          <div className="p-5 space-y-5">
            {/* Stats strip — 2x2 so drops sit alongside points (both are
                customer-visible ledgers; surfacing both keeps ops aligned
                with what the customer sees in-app and at the till). */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#1a1a1a] rounded-lg px-3 py-3 text-center">
                <p className="text-xl font-semibold text-white">{c.order_count}</p>
                <p className="text-gray-600 text-xs mt-0.5">Orders</p>
              </div>
              <div className="bg-[#1a1a1a] rounded-lg px-3 py-3 text-center">
                <p className="text-lg font-semibold text-white">{formatCurrency(c.total_spent_fils)}</p>
                <p className="text-gray-600 text-xs mt-0.5">Total Spent</p>
              </div>
              <div className="bg-[#1a1a1a] rounded-lg px-3 py-3 text-center">
                <p className="text-xl font-semibold text-white">{c.loyalty_points.toLocaleString()}</p>
                <p className="text-gray-600 text-xs mt-0.5">Loyalty Points</p>
              </div>
              <div className="bg-[#1a1a1a] rounded-lg px-3 py-3 text-center">
                <p className="text-xl font-semibold text-white">{(c.drops_balance ?? 0).toLocaleString()}</p>
                <p className="text-gray-600 text-xs mt-0.5">Drops</p>
              </div>
            </div>

            {/* Info */}
            <div className="bg-[#1a1a1a] rounded-lg px-4 py-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Plan</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.plan === 'premium' ? 'bg-yellow-500/15 text-yellow-400' : 'bg-gray-500/15 text-gray-400'}`}>
                  {c.plan === 'premium' ? '★ Premium' : 'Free'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status</span>
                {c.frozen
                  ? <span className="text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">Frozen</span>
                  : <span className="text-xs bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">Active</span>
                }
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">VIP</span>
                {c.is_vip
                  ? <span className="text-xs bg-[#C41E3A]/15 text-[#C41E3A] px-2 py-0.5 rounded-full font-medium">★ VIP · Vintage + discount</span>
                  : <span className="text-xs bg-gray-500/15 text-gray-400 px-2 py-0.5 rounded-full">No</span>
                }
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Member since</span>
                <span className="text-gray-300">{formatDate(c.created_at)}</span>
              </div>
            </div>

            {/* Store credit */}
            <StoreCreditSection customerId={c.id} canManage={canManage} />

            {/* Actions */}
            {canManage && (
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Actions</p>
                <div className="space-y-2">
                  <button
                    onClick={() => toggleFreeze.mutate({ id: c.id, frozen: !c.frozen })}
                    disabled={toggleFreeze.isPending}
                    className={`w-full text-sm py-2 rounded-lg border transition-colors disabled:opacity-50 ${
                      c.frozen
                        ? 'border-green-500/30 text-green-400 hover:bg-green-500/10'
                        : 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                    }`}
                  >
                    {toggleFreeze.isPending ? '…' : c.frozen ? 'Unfreeze Account' : 'Freeze Account'}
                  </button>
                  <button
                    onClick={() => {
                      if (!window.confirm(
                        `Sign ${c.full_name || 'this customer'} out of every device? They'll need to log in again on their trusted device. This is instant and doesn't touch their password.`,
                      )) return
                      revokeSessions.mutate(c.id)
                    }}
                    disabled={revokeSessions.isPending}
                    className="w-full text-sm py-2 rounded-lg border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 transition-colors disabled:opacity-50"
                  >
                    {revokeSessions.isPending ? '…' : 'Revoke sessions'}
                  </button>
                  {revokedAt && !revokeSessions.isPending && (
                    <p className="text-[11px] text-green-400 text-center">
                      ✓ Signed out on all devices · {revokedAt.toLocaleTimeString('en-GB', { timeZone: 'Asia/Amman' })}
                    </p>
                  )}
                  {revokeSessions.error != null && (
                    <p className="text-[11px] text-red-400 text-center">
                      Failed. Check network + try again.
                    </p>
                  )}
                  {/* VIP toggle — super-admin only. Enabling raises the
                      customer to Vintage (grants the loyalty points, no
                      drops) + turns on the auto-discount. Disabling keeps
                      the points (stays Vintage) and only drops the
                      discount. */}
                  {canVip && (
                    <button
                      onClick={() => {
                        const enabling = !c.is_vip
                        const msg = enabling
                          ? `Make ${c.full_name || 'this customer'} a VIP? They'll be raised to Vintage (we grant the loyalty points to get there, no drops) and get the automatic VIP discount on future orders.`
                          : `Remove VIP from ${c.full_name || 'this customer'}? They keep their points and stay Vintage, but lose the VIP discount.`
                        if (!window.confirm(msg)) return
                        toggleVip.mutate({ id: c.id, is_vip: enabling })
                      }}
                      disabled={toggleVip.isPending}
                      className={`w-full text-sm py-2 rounded-lg border transition-colors disabled:opacity-50 ${
                        c.is_vip
                          ? 'border-gray-500/30 text-gray-300 hover:bg-gray-500/10'
                          : 'border-[#C41E3A]/40 text-[#C41E3A] hover:bg-[#C41E3A]/10'
                      }`}
                    >
                      {toggleVip.isPending ? '…' : c.is_vip ? 'Remove VIP status' : 'Make VIP'}
                    </button>
                  )}
                  {toggleVip.error != null && (
                    <p className="text-[11px] text-red-400 text-center">
                      VIP update failed. Check network + try again.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Order history */}
            <section>
              <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Recent Orders</p>
              {data!.orders.length === 0 ? (
                <p className="text-gray-600 text-sm text-center py-4">No orders yet</p>
              ) : (
                <div className="space-y-2">
                  {data!.orders.map(order => (
                    <div key={order.id} className="bg-[#1a1a1a] rounded-lg px-3 py-2.5 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-mono">{order.order_number}</p>
                        <p className="text-gray-600 text-xs mt-0.5">{formatDateTime(order.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-300 text-sm">{formatCurrency(order.total_fils)}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[order.status] ?? 'bg-gray-500/15 text-gray-400'}`}>
                          {STATUS_LABELS[order.status] ?? order.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Insights — category mix / address pattern / cadence.
                Nadine ask 2026-10-06: ops needs to see what the customer
                buys, where it goes, and how often, without reconciling
                the Orders tab by hand. Separate section (not a tab) so
                it co-exists with Recent Orders on one scroll. */}
            <CustomerInsightsSection customerId={c.id} />
          </div>
        )}
      </div>
    </div>
  )
}

interface CustomerInsights {
  user: { id: string; full_name: string; signed_up_at: string }
  summary: {
    order_count: number
    total_spent_fils: number
    first_order_at: string | null
    last_order_at: string | null
  }
  category_mix: Array<{
    id: string; slug: string; name_en: string; name_ar: string
    units: number; net_revenue_fils: number; order_count: number
  }>
  address_pattern: Array<{
    address: string; order_count: number; total_fils: number; last_used_at: string
  }>
  cadence: {
    weekly: Array<{ period: string; orders: number; total_fils: number }>
    monthly: Array<{ period: string; orders: number; total_fils: number }>
  }
}

function CustomerInsightsSection({ customerId }: { customerId: string }) {
  const { data, isLoading, isError } = useQuery<CustomerInsights>({
    queryKey: ['customer-insights', customerId],
    queryFn: () => api.get(`/customers/${customerId}/insights`).then(r => r.data),
  })

  if (isLoading) {
    return (
      <section>
        <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Insights</p>
        <p className="text-gray-600 text-sm text-center py-4">Loading insights…</p>
      </section>
    )
  }
  if (isError || !data) {
    return (
      <section>
        <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Insights</p>
        <p className="text-red-400 text-sm">Failed to load insights.</p>
      </section>
    )
  }
  if (data.summary.order_count === 0) {
    return (
      <section>
        <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Insights</p>
        <p className="text-gray-600 text-sm text-center py-4">Nothing to show yet — no completed orders.</p>
      </section>
    )
  }

  // Monthly cadence average over the window of months with any orders —
  // "months active" is more honest than dividing by elapsed-since-signup
  // for a customer who's been quiet. Weekly same.
  const monthlyActive = data.cadence.monthly.filter(m => m.orders > 0)
  const weeklyActive = data.cadence.weekly.filter(w => w.orders > 0)
  const avgPerMonth = monthlyActive.length > 0
    ? (monthlyActive.reduce((s, m) => s + m.orders, 0) / monthlyActive.length)
    : 0
  const avgPerWeek = weeklyActive.length > 0
    ? (weeklyActive.reduce((s, w) => s + w.orders, 0) / weeklyActive.length)
    : 0
  const maxMonthlyOrders = Math.max(1, ...data.cadence.monthly.map(m => m.orders))
  const maxCategoryRev = Math.max(1, ...data.category_mix.map(c => c.net_revenue_fils))

  return (
    <section className="space-y-4">
      <p className="text-xs text-gray-600 uppercase tracking-wider">Insights</p>

      {/* Cadence summary — two numbers that answer "how often do they order?" */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#1a1a1a] rounded-lg px-3 py-3 text-center">
          <p className="text-xl font-semibold text-white">{avgPerWeek.toFixed(1)}</p>
          <p className="text-gray-600 text-xs mt-0.5">Avg orders / week</p>
        </div>
        <div className="bg-[#1a1a1a] rounded-lg px-3 py-3 text-center">
          <p className="text-xl font-semibold text-white">{avgPerMonth.toFixed(1)}</p>
          <p className="text-gray-600 text-xs mt-0.5">Avg orders / month</p>
        </div>
      </div>

      {/* Monthly bar chart — last 12 months. Zero-months render as a
          faint rail so the gap between orders is visible. */}
      {data.cadence.monthly.length > 0 && (
        <div className="bg-[#1a1a1a] rounded-lg px-4 py-3">
          <p className="text-xs text-gray-500 mb-2">Orders per month</p>
          <div className="flex items-end gap-1 h-20">
            {data.cadence.monthly.map(m => {
              const h = Math.max(3, Math.round((m.orders / maxMonthlyOrders) * 72))
              return (
                <div
                  key={m.period}
                  className="flex-1 flex flex-col items-center justify-end group relative"
                  title={`${m.period} — ${m.orders} order${m.orders === 1 ? '' : 's'} · ${formatCurrency(m.total_fils)}`}
                >
                  <div
                    className="w-full bg-[#C41E3A]/70 rounded-sm transition-colors group-hover:bg-[#C41E3A]"
                    style={{ height: `${h}px` }}
                  />
                  <p className="text-[9px] text-gray-600 mt-1">{m.period.slice(5)}</p>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Category mix — top 10 by net revenue with a horizontal bar
          so proportions read at a glance. */}
      {data.category_mix.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2">Top categories</p>
          <div className="space-y-1.5">
            {data.category_mix.map(cm => {
              const pct = Math.round((cm.net_revenue_fils / maxCategoryRev) * 100)
              return (
                <div key={cm.id} className="bg-[#1a1a1a] rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white text-sm">{cm.name_en}</span>
                    <span className="text-gray-400 text-xs">
                      {cm.units} unit{cm.units === 1 ? '' : 's'} · {formatCurrency(cm.net_revenue_fils)}
                    </span>
                  </div>
                  <div className="h-1 bg-[#2e2e2e] rounded-full overflow-hidden">
                    <div className="h-full bg-[#C41E3A]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Address pattern — delivery-channel only; empty for POS-only
          customers (walk-in has no address). */}
      {data.address_pattern.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2">Top delivery addresses</p>
          <div className="space-y-1.5">
            {data.address_pattern.map(ap => (
              <div key={ap.address} className="bg-[#1a1a1a] rounded-lg px-3 py-2">
                <p className="text-white text-sm leading-snug">{ap.address}</p>
                <p className="text-gray-600 text-xs mt-0.5">
                  {ap.order_count} order{ap.order_count === 1 ? '' : 's'} · {formatCurrency(ap.total_fils)}
                  {' · '}last {formatDate(ap.last_used_at)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

export default function Customers() {
  const { can } = useAuth()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [claimOpen, setClaimOpen] = useState(false)
  const [sort, setSort] = useState<string>('joined')
  const [order, setOrder] = useState<'asc' | 'desc'>('desc')
  const [filter, setFilter] = useState<string>('')

  // Click a sortable column header: toggle direction if it's the active
  // column, otherwise switch to it (default desc — most-first reads best
  // for orders/spend/points).
  function toggleSort(col: string) {
    if (sort === col) setOrder(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSort(col); setOrder('desc') }
    setPage(1)
  }

  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (filter) params.set('filter', filter)
  params.set('sort', sort)
  params.set('order', order)
  params.set('page', String(page))

  const { data, isError } = useQuery<{ customers: Customer[]; total: number; limit: number }>({
    queryKey: ['customers', search, filter, sort, order, page],
    queryFn: () => api.get(`/customers?${params}`).then(r => r.data),
  })

  const totalPages = Math.ceil((data?.total ?? 0) / (data?.limit ?? 50))
  const canManage = can(['super_admin', 'operations'])
  const canVip = can(['super_admin'])

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-lg font-semibold text-white">Customers</h1>
        <div className="flex items-center gap-2">
          {canManage && (
            <button
              type="button"
              onClick={() => setClaimOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#3a3a3a] bg-[#2a2a2a] px-3 py-1.5 text-xs font-medium text-gray-200 transition-colors hover:bg-[#333]"
              title="Create a customer account by phone and SMS them a password-set link"
            >
              <Send size={12} />
              Send claim link
            </button>
          )}
          <CsvControls
            basePath="/customers"
            templateFilename="customers-template.csv"
            exportFilename={`customers-export-${new Date().toISOString().slice(0, 10)}.csv`}
            label="customer"
            onImported={() => qc.invalidateQueries({ queryKey: ['customers'] })}
          />
        </div>
      </div>
      {claimOpen && (
        <ClaimLinkModal
          onClose={() => setClaimOpen(false)}
          onSent={() => {
            setClaimOpen(false)
            qc.invalidateQueries({ queryKey: ['customers'] })
          }}
        />
      )}
      {isError && <div className="mb-4 text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-3">Failed to load customers. Check your connection or try refreshing.</div>}

      <div className="flex gap-3 mb-5 items-center">
        <input
          value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
          placeholder="Search name, phone…"
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] w-64"
        />
        <select
          value={filter}
          onChange={e => { setFilter(e.target.value); setPage(1) }}
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          title="Filter the customer list"
        >
          <option value="">All customers</option>
          <option value="vip">VIP</option>
          <option value="premium">Premium plan</option>
          <option value="has_email">Has email</option>
          <option value="frozen">Frozen</option>
        </select>
        {(search || filter) && (
          <button
            type="button"
            onClick={() => { setSearch(''); setFilter(''); setPage(1) }}
            className="text-xs text-gray-500 hover:text-gray-300 px-2 py-2"
          >
            Clear
          </button>
        )}
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
              {([
                ['Customer', 'name'],
                ['Plan', null],
                ['Orders', 'orders'],
                ['Total Spent', 'spent'],
                ['Points', 'points'],
                ['Drops', 'drops'],
                ['Joined', 'joined'],
                ['Status', null],
              ] as [string, string | null][]).map(([label, col]) => (
                <th
                  key={label}
                  onClick={col ? () => toggleSort(col) : undefined}
                  className={`px-4 py-3 text-left font-medium ${col ? 'cursor-pointer select-none hover:text-gray-300' : ''}`}
                >
                  {label}{col && sort === col ? (order === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.customers.map((c) => (
              <tr
                key={c.id}
                className={`border-b border-[#222] hover:bg-white/2 transition-colors cursor-pointer ${c.frozen ? 'opacity-60' : ''}`}
                onClick={() => setSelectedId(c.id)}
              >
                <td className="px-4 py-3">
                  <p className="text-white">{c.full_name}</p>
                  <p className="text-gray-500 text-xs">{c.phone}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.plan === 'premium' ? 'bg-yellow-500/15 text-yellow-400' : 'bg-gray-500/15 text-gray-400'}`}>
                    {c.plan === 'premium' ? '★ Premium' : 'Free'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-300">{c.order_count}</td>
                <td className="px-4 py-3 text-gray-300">{formatCurrency(c.total_spent_fils)}</td>
                <td className="px-4 py-3 text-gray-400">{c.loyalty_points.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-400">{(c.drops_balance ?? 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(c.created_at)}</td>
                <td className="px-4 py-3">
                  {c.frozen
                    ? <span className="text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">Frozen</span>
                    : <span className="text-xs bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">Active</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#2e2e2e] text-xs text-gray-500">
            <span>{data?.total} total customers</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded bg-[#2e2e2e] disabled:opacity-40 hover:bg-[#3a3a3a] transition-colors">Prev</button>
              <span className="px-2 py-1">{page} / {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded bg-[#2e2e2e] disabled:opacity-40 hover:bg-[#3a3a3a] transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {selectedId && (
        <CustomerPanel
          customerId={selectedId}
          onClose={() => setSelectedId(null)}
          canManage={canManage}
          canVip={canVip}
        />
      )}
    </div>
  )
}

interface StoreCreditTx {
  id: string
  amount_fils: number
  source: string
  source_order_id: string | null
  notes: string | null
  expires_at: string | null
  created_at: string
  created_by_name: string | null
}

interface StoreCreditResponse {
  balance_fils: number
  transactions: StoreCreditTx[]
}

const STORE_CREDIT_SOURCES = [
  { value: 'refund_cod', label: 'COD refund' },
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'admin_adjust', label: 'Manual adjustment' },
] as const

function StoreCreditSection({ customerId, canManage }: { customerId: string; canManage: boolean }) {
  const qc = useQueryClient()
  const { data } = useQuery<StoreCreditResponse>({
    queryKey: ['customer-store-credit', customerId],
    queryFn: () => api.get(`/customers/${customerId}/store-credit`).then(r => r.data),
  })
  const [addOpen, setAddOpen] = useState(false)
  const balance = data?.balance_fils ?? 0
  const recent = (data?.transactions ?? []).slice(0, 5)

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-600 uppercase tracking-wider">Store credit</p>
        {canManage && (
          <button
            onClick={() => setAddOpen(true)}
            className="text-xs text-[#C41E3A] hover:underline"
          >
            + Add credit
          </button>
        )}
      </div>
      <div className="bg-[#1a1a1a] rounded-lg px-4 py-3">
        <p className="text-xl font-semibold text-white">{formatCurrency(balance)}</p>
        <p className="text-gray-600 text-xs mt-0.5">Available balance</p>
        {recent.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#2e2e2e] space-y-1.5">
            {recent.map(tx => (
              <div key={tx.id} className="flex items-center justify-between text-xs">
                <div className="flex-1 truncate">
                  <span className="text-gray-300">{tx.source}</span>
                  {tx.notes && <span className="text-gray-600"> · {tx.notes}</span>}
                </div>
                <span className={`font-mono ml-2 ${tx.amount_fils > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {tx.amount_fils > 0 ? '+' : '−'}{formatCurrency(Math.abs(tx.amount_fils))}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {addOpen && (
        <AddStoreCreditModal
          customerId={customerId}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['customer-store-credit', customerId] })
            setAddOpen(false)
          }}
        />
      )}
    </section>
  )
}

function AddStoreCreditModal({
  customerId,
  onClose,
  onSaved,
}: {
  customerId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [amount, setAmount] = useState('')
  const [source, setSource] = useState<string>('refund_cod')
  const [sourceOrderId, setSourceOrderId] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const jd = parseFloat(amount)
    if (!Number.isFinite(jd) || jd <= 0) {
      setError('Amount must be a positive number.')
      return
    }
    const fils = Math.round(jd * 1000)
    setSubmitting(true)
    try {
      await api.post(`/customers/${customerId}/store-credit`, {
        amount_fils: fils,
        source,
        source_order_id: sourceOrderId.trim() || null,
        notes: notes.trim() || null,
      })
      onSaved()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? 'Couldn\'t add credit.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-sm bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm font-semibold">Add store credit</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div>
          <label className="text-xs text-gray-500">Amount (JOD)</label>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="e.g. 5.000"
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            autoFocus
          />
        </div>

        <div>
          <label className="text-xs text-gray-500">Reason</label>
          <select
            value={source}
            onChange={e => setSource(e.target.value)}
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          >
            {STORE_CREDIT_SOURCES.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {source === 'refund_cod' && (
          <div>
            <label className="text-xs text-gray-500">Source order ID (optional)</label>
            <input
              type="text"
              value={sourceOrderId}
              onChange={e => setSourceOrderId(e.target.value)}
              placeholder="Original order id"
              className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#C41E3A]"
            />
          </div>
        )}

        <div>
          <label className="text-xs text-gray-500">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Surfaced to the customer on the wallet history."
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A] resize-none"
          />
        </div>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 text-sm py-2 rounded-lg bg-[#C41E3A] text-white font-medium hover:bg-[#a01830] disabled:opacity-50"
          >
            {submitting ? 'Adding…' : 'Add credit'}
          </button>
        </div>
      </form>
    </div>
  )
}

// POST /admin/customers/claim-link — enrol by phone + fire the welcome
// SMS with the drinx.jo/claim link. Idempotent on phone: re-firing for
// an existing customer just re-sends the SMS, so this is also the
// "resend my claim link" tool when a customer tells ops they never
// finished setting up.
function ClaimLinkModal({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const [phone, setPhone] = useState('')
  const [fullName, setFullName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ already_existed: boolean } | null>(null)

  const mut = useMutation({
    mutationFn: () => api.post('/customers/claim-link', {
      phone: phone.trim(),
      full_name: fullName.trim() || undefined,
    }).then(r => r.data as { user_id: string; already_existed: boolean }),
    onSuccess: (data) => {
      setResult({ already_existed: data.already_existed })
      // Keep the success message on screen for 2s before closing so
      // ops has a chance to read it, then refresh the list.
      setTimeout(onSent, 2000)
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } }; message?: string })
        .response?.data?.message ?? (e as { message?: string }).message ?? 'Could not send the claim link.'
      setError(msg)
    },
  })

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-md">
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <div>
            <h2 className="text-sm font-semibold text-white">Send claim link</h2>
            <p className="text-xs text-gray-500 mt-1">
              Creates a DrinX account for this phone and sends a welcome SMS with a password-set link. Use when a customer tried to sign up but the OTP never arrived.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-1">
            <X size={16} />
          </button>
        </div>
        <form
          onSubmit={e => { e.preventDefault(); setError(null); mut.mutate() }}
          className="p-5 space-y-4"
        >
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Phone (Jordan mobile)</label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+962 77/78/79…"
              className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] font-mono"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Full name (optional)</label>
            <input
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder="Customer's name"
              className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A]"
            />
          </div>
          {result && (
            <div className="rounded-md border border-green-500/30 bg-green-500/10 px-3 py-2 text-xs text-green-300">
              {result.already_existed
                ? 'Customer already existed — claim-link SMS re-sent.'
                : 'New customer created — welcome SMS with claim link is queued.'}
            </div>
          )}
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-[#2e2e2e]">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-400 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mut.isPending || !phone.trim() || !!result}
              className="inline-flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#a01a30] text-white text-xs rounded-md px-3 py-1.5 disabled:opacity-40"
            >
              <Send size={12} />
              {mut.isPending ? 'Sending…' : 'Send claim link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
